import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Crown, Check, Star, ArrowRight, CreditCard,
  Gift, CurrencyBtc,
  Globe, ShieldCheck, Wallet, ArrowsLeftRight
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const Benefits = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('loyalty');

  // Membership Tiers - Member → Reserve → Founding
  const tiers = [
    {
      name: 'Member',
      points: '0 - 999 pts',
      color: '#8BA89A',
      description: t('benefits_tier_member'),
      perks: [
        'Accès à tous les marchands',
        '2 pts/£1 réseau habitué',
        '1 pt/£1 partout ailleurs',
        'Cartes cadeaux universelles',
        'App mobile complète',
        'Support par email'
      ],
      highlight: false
    },
    {
      name: 'Reserve',
      points: '1000 - 4999 pts',
      color: '#C0C0C0',
      description: t('benefits_tier_reserve'),
      perks: [
        'Tout Member +',
        'Bonus points x1.5',
        'Offres exclusives',
        'Accès prioritaire nouveautés',
        'Support prioritaire',
        'Events membres Reserve'
      ],
      highlight: false
    },
    {
      name: 'Founding',
      points: '5000+ pts',
      color: '#C3A365',
      description: t('benefits_tier_founding'),
      perks: [
        'Tout Reserve +',
        'Bonus points x2',
        'Carte Or métal exclusive',
        'Cadeaux surprise mensuels',
        'Events VIP privés',
        'Conciergerie dédiée',
        'Accès Charge Card prioritaire'
      ],
      highlight: true
    }
  ];

  // Charge Card Variants
  const cardVariants = [
    { name: 'Obsidian', color: '#1C1C1C', textColor: '#fff', desc: t('benefits_card_obsidian') },
    { name: 'Crème', color: '#F5F0E6', textColor: '#1C1C1C', desc: t('benefits_card_creme') },
    { name: 'Forêt', color: '#145A3A', textColor: '#C3A365', desc: t('benefits_card_forest') },
    { name: 'Or', color: '#C3A365', textColor: '#1C1C1C', desc: t('benefits_card_gold'), limited: true }
  ];

  // Crypto currencies
  const cryptos = [
    { symbol: 'BTC', name: 'Bitcoin', color: '#F7931A', desc: t('benefits_crypto_btc') },
    { symbol: 'ETH', name: 'Ethereum', color: '#627EEA', desc: t('benefits_crypto_eth') },
    { symbol: 'USDC', name: 'USD Coin', color: '#2775CA', desc: t('benefits_crypto_usdc') },
    { symbol: 'SOL', name: 'Solana', color: '#9945FF', desc: t('benefits_crypto_sol'), coming: 'Q4 2026' }
  ];

  const tabs = [
    { id: 'loyalty', label: t('benefits_tab_loyalty'), icon: Star },
    { id: 'card', label: t('benefits_tab_card'), icon: CreditCard },
    { id: 'crypto', label: t('benefits_tab_crypto'), icon: CurrencyBtc }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Crown size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('benefits_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('benefits_title_1')}<br />
            <span className="text-[#C3A365]">{t('benefits_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('benefits_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="px-6 pb-8">
        <div className="max-w-xl mx-auto">
          <div className="flex bg-[#1E2F26] p-1.5 rounded-xl">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    isActive
                      ? 'bg-[#C3A365] text-[#0F1A14]'
                      : 'text-[#8BA89A] hover:text-white'
                  }`}
                  data-testid={`benefits-tab-${tab.id}`}
                >
                  <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOYALTY TAB */}
      {activeTab === 'loyalty' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Points Explanation */}
          <section className="px-6 pb-16">
            <div className="max-w-4xl mx-auto">
              <div className="bg-[#18261E] border border-[#283D32] p-8 text-center mb-12">
                <h3 className="text-2xl font-light mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('benefits_how_earn')}
                </h3>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div className="p-4">
                    <p className="text-4xl text-[#C3A365] font-light mb-2">2 pts</p>
                    <p className="text-white font-medium">{t('points_by')} £1</p>
                    <p className="text-[#8BA89A] text-sm mt-1">{t('benefits_pts_network')}</p>
                  </div>
                  <div className="p-4 border-x border-[#283D32]">
                    <p className="text-4xl text-[#8BA89A] font-light mb-2">1 pt</p>
                    <p className="text-white font-medium">{t('points_by')} £1</p>
                    <p className="text-[#8BA89A] text-sm mt-1">{t('benefits_pts_elsewhere')}</p>
                  </div>
                  <div className="p-4">
                    <p className="text-4xl text-white font-light mb-2">100 pts</p>
                    <p className="text-[#C3A365] font-medium">= £1</p>
                    <p className="text-[#8BA89A] text-sm mt-1">{t('benefits_pts_value')}</p>
                  </div>
                </div>
              </div>

              {/* Tiers */}
              <div className="grid lg:grid-cols-3 gap-6">
                {tiers.map((tier, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative p-8 border transition-all ${
                      tier.highlight
                        ? 'bg-gradient-to-b from-[#C3A365]/15 to-transparent border-[#C3A365]/50'
                        : 'bg-[#1E2F26]/30 border-[#283D32] hover:border-[#3E5248]'
                    }`}
                  >
                    {tier.highlight && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#C3A365] text-[#0F1A14] text-xs font-bold uppercase tracking-wider">
                        {t('benefits_exclusive')}
                      </div>
                    )}

                    <div className="text-center mb-8">
                      <Crown size={40} style={{ color: tier.color }} weight="fill" className="mx-auto mb-4" />
                      <h3 className="text-2xl font-medium mb-1" style={{ color: tier.color }}>{tier.name}</h3>
                      <p className="text-[#8BA89A] text-sm mb-2">{tier.points}</p>
                      <p className="text-white text-sm">{tier.description}</p>
                    </div>

                    <ul className="space-y-3">
                      {tier.perks.map((perk, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check size={18} style={{ color: tier.color }} weight="bold" className="flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-white">{perk}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </motion.div>
      )}

      {/* CHARGE CARD TAB */}
      {activeTab === 'card' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <section className="px-6 pb-16">
            <div className="max-w-5xl mx-auto">
              {/* Card Hero */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-6">
                  <CreditCard size={16} className="text-[#C3A365]" />
                  <span className="text-sm text-[#8BA89A]">{t('benefits_card_hero_badge')}</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('benefits_card_title')}
                </h2>
                <p className="text-[#8BA89A] max-w-xl mx-auto">
                  {t('benefits_card_desc')}
                </p>
              </div>

              {/* Card Variants */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
                {cardVariants.map((card, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="relative group"
                  >
                    {card.limited && (
                      <div className="absolute -top-2 -right-2 z-10 px-2 py-1 bg-[#C3A365] text-[#0F1A14] text-[10px] font-bold uppercase">
                        {t('benefits_card_limited')}
                      </div>
                    )}
                    <div 
                      className="aspect-[1.6/1] rounded-xl flex items-end p-4 transition-transform group-hover:scale-105"
                      style={{ backgroundColor: card.color }}
                    >
                      <div>
                        <p className="text-xs opacity-60" style={{ color: card.textColor }}>habitué</p>
                        <p className="font-medium" style={{ color: card.textColor }}>{card.name}</p>
                      </div>
                    </div>
                    <p className="text-center text-[#8BA89A] text-xs mt-2">{card.desc}</p>
                  </motion.div>
                ))}
              </div>

              {/* Card Features */}
              <div className="grid md:grid-cols-3 gap-6 mb-16">
                {[
                  { icon: Globe, title: t('benefits_card_feature_1'), desc: t('benefits_card_feature_1_desc') },
                  { icon: ShieldCheck, title: t('benefits_card_feature_2'), desc: t('benefits_card_feature_2_desc') },
                  { icon: ArrowsLeftRight, title: t('benefits_card_feature_3'), desc: t('benefits_card_feature_3_desc') }
                ].map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <div key={i} className="p-6 bg-[#1E2F26]/50 border border-[#283D32]">
                      <Icon size={28} className="text-[#C3A365] mb-4" weight="duotone" />
                      <h4 className="text-white font-medium mb-2">{feature.title}</h4>
                      <p className="text-[#8BA89A] text-sm">{feature.desc}</p>
                    </div>
                  );
                })}
              </div>

              {/* Pricing */}
              <div className="bg-[#18261E] border border-[#283D32] p-8 text-center">
                <h3 className="text-xl font-medium text-white mb-6">{t('benefits_card_fees_title')}</h3>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <p className="text-3xl text-white font-light">£120</p>
                    <p className="text-[#8BA89A] text-sm">{t('benefits_card_fees_standard')}</p>
                  </div>
                  <span className="text-[#3E5248]">—</span>
                  <div className="text-center">
                    <p className="text-3xl text-[#C3A365] font-light">£250</p>
                    <p className="text-[#C3A365] text-sm">{t('benefits_card_fees_gold')}</p>
                  </div>
                </div>
                <p className="text-[#8BA89A] text-sm mt-6">
                  {t('benefits_card_fees_merchant')}
                </p>
              </div>
            </div>
          </section>
        </motion.div>
      )}

      {/* CRYPTO TAB */}
      {activeTab === 'crypto' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <section className="px-6 pb-16">
            <div className="max-w-4xl mx-auto">
              {/* Crypto Hero */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-6">
                  <CurrencyBtc size={16} className="text-[#C3A365]" />
                  <span className="text-sm text-[#8BA89A]">{t('benefits_crypto_badge')}</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('benefits_crypto_title')}
                </h2>
                <p className="text-[#8BA89A] max-w-xl mx-auto">
                  {t('benefits_crypto_desc')}
                </p>
              </div>

              {/* Supported Cryptos */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
                {cryptos.map((crypto, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className={`relative p-6 text-center border ${
                      crypto.coming 
                        ? 'bg-[#1E2F26]/30 border-[#283D32] border-dashed' 
                        : 'bg-[#1E2F26] border-[#283D32]'
                    }`}
                  >
                    {crypto.coming && (
                      <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#283D32] text-[#8BA89A] text-[10px] rounded">
                        {crypto.coming}
                      </div>
                    )}
                    <div 
                      className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: crypto.color }}
                    >
                      {crypto.symbol.charAt(0)}
                    </div>
                    <p className="text-white font-medium">{crypto.symbol}</p>
                    <p className="text-[#8BA89A] text-xs">{crypto.desc}</p>
                  </motion.div>
                ))}
              </div>

              {/* How it works */}
              <div className="bg-[#18261E] border border-[#283D32] p-8 mb-12">
                <h3 className="text-xl font-medium text-white mb-6 text-center">{t('benefits_crypto_how_title')}</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { num: '1', title: t('benefits_crypto_step_1'), desc: t('benefits_crypto_step_1_desc') },
                    { num: '2', title: t('benefits_crypto_step_2'), desc: t('benefits_crypto_step_2_desc') },
                    { num: '3', title: t('benefits_crypto_step_3'), desc: t('benefits_crypto_step_3_desc') }
                  ].map((step, i) => (
                    <div key={i} className="text-center">
                      <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-3">
                        <span className="text-[#C3A365] font-bold">{step.num}</span>
                      </div>
                      <h4 className="text-white font-medium mb-1">{step.title}</h4>
                      <p className="text-[#8BA89A] text-sm">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key Points */}
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Star, text: t('benefits_crypto_key_1') },
                  { icon: Wallet, text: t('benefits_crypto_key_2') },
                  { icon: CreditCard, text: t('benefits_crypto_key_3') },
                  { icon: ShieldCheck, text: t('benefits_crypto_key_4') }
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-4 bg-[#1E2F26]/50 border border-[#283D32]">
                      <Icon size={20} className="text-[#C3A365]" />
                      <span className="text-white text-sm">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </motion.div>
      )}

      {/* Gift Cards Summary */}
      <section className="px-6 py-16 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-3 py-1.5 rounded-full mb-4">
                <Gift size={14} className="text-[#C3A365]" />
                <span className="text-xs text-[#C3A365]">{t('benefits_giftcard_badge')}</span>
              </div>
              <h3 className="text-2xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('benefits_giftcard_title')}
              </h3>
              <ul className="space-y-3 text-[#8BA89A]">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-[#C3A365]" />
                  <span>{t('benefits_giftcard_amounts')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-[#C3A365]" />
                  <span>{t('benefits_giftcard_validity')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-[#C3A365]" />
                  <span>{t('benefits_giftcard_network')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-[#C3A365]" />
                  <span>{t('benefits_giftcard_points')}</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/cartes-cadeaux')}
                className="mt-6 inline-flex items-center gap-2 text-[#C3A365] font-medium hover:gap-3 transition-all"
                data-testid="benefits-giftcard-more"
              >
                {t('benefits_giftcard_more')} <ArrowRight size={16} />
              </button>
            </div>
            <div className="relative">
              <div className="aspect-[1.6/1] bg-gradient-to-br from-[#145A3A] to-[#0F1A14] rounded-xl p-6 flex flex-col justify-between">
                <div className="flex justify-between">
                  <span className="brand-logo text-white/80">habitué</span>
                  <Gift size={24} className="text-[#C3A365]/50" />
                </div>
                <div>
                  <p className="text-white/50 text-xs">{t('gift_card_label')}</p>
                  <p className="text-2xl text-white font-light">£10 – £200</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('benefits_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('benefits_cta_desc')}
          </p>
          <motion.button
            onClick={() => navigate('/activer')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="benefits-cta-btn"
          >
            {t('benefits_cta_btn')} <ArrowRight size={18} />
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Benefits;
